import { useMemo } from 'react';
import {
  Box,
  Typography,
  Divider,
  FaRegFileCode,
  AiOutlineAndroid,
  UxMason,
  GridRowsProp,
  ControllerDataGrid as DataGrid,
  GridColDef,
  Link,
} from '@bymason-private/legacy-pattern-components';
import { Route, RouterLink } from '@bymason-private/route-provider';
import { DeviceModel } from '@bymason/controller-api-hooks';
import {
  LegacyDetailsList,
  DetailItem,
  DetailsListEmpty,
  UpdatedAlertWithTooltip,
} from '@bymason-private/controller-shared-widgets';

const appsGridCols: GridColDef[] = [
  { field: 'application', headerName: 'Application', flex: 3 },
  { field: 'version', headerName: 'Version', flex: 1 },
];

const subSectionSx = {
  display: 'flex',
  '& > *': {
    flex: '1 1 auto',
  },
};

const subSectionTitleSx = {
  flex: '0 0 120px',
  fontWeight: '700',
  padding: '9px',
  overflowX: 'hidden',
};

export function SoftwareInfoView({ deviceInfo, hideTitleIcons, projectViewRoute }: SoftwareInfoViewProps) {
  const appsGridRows = useMemo<GridRowsProp>(() => {
    const apps = deviceInfo?.apps || [];

    return apps.map((appInfo, id) => {
      const { application, version } = appInfo;

      return { id, application, version };
    });
  }, [deviceInfo]);

  const configItems = useMemo<DetailItem[]>(() => {
    const name = deviceInfo?.os.configuration.project?.replace('unknown', '');
    const version = deviceInfo?.os.configuration.version?.replace('unknown', '');
    const status = deviceInfo?.status.projectUpdated;
    const projectRoute = projectViewRoute && { ...projectViewRoute, path: `/projects/:name` };
    const projectVersionRoute = projectViewRoute && {
      ...projectViewRoute,
      path: `/projects/:name#${version}`,
    };

    return [
      {
        label: 'Name',
        value: name ? (
          projectRoute ? (
            <RouterLink
              linkComponent={<Link className='MuiDataGrid-cellContent'>{name}</Link>}
              route={projectRoute}
              state={{ name }}
            />
          ) : (
            name
          )
        ) : (
          'Unknown'
        ),
      },
      {
        label: 'Version',
        value:
          name && version ? (
            projectVersionRoute ? (
              <RouterLink
                linkComponent={<Link className='MuiDataGrid-cellContent'>{version}</Link>}
                route={projectVersionRoute}
                state={{ name }}
              />
            ) : (
              version
            )
          ) : (
            'Unknown'
          ),
      },
      {
        label: 'Status',
        value: <UpdatedAlertWithTooltip value={status} statusType='project' />,
      },
    ];
  }, [deviceInfo, projectViewRoute]);

  const osItems = useMemo<DetailItem[]>(() => {
    const version = deviceInfo?.os.masonOS.version?.replace('unknown', '')
      ? deviceInfo.os.masonOS.version
      : undefined;
    const release = deviceInfo?.os.masonOS.release?.replace('unknown', '');
    const status = deviceInfo?.status.osUpdated;

    return [
      { label: 'Mason OS Version', value: version || 'Unknown' },
      { label: 'Android OS Version', value: release || 'Unknown' },
      {
        label: 'Status',
        value: <UpdatedAlertWithTooltip value={status} statusType='os' />,
      },
    ];
  }, [deviceInfo]);

  return (
    <div>
      <Box pt={2}>
        <div>
          <Typography variant='h5'>Application</Typography>
        </div>
        <Box sx={subSectionSx}>
          <Box display={hideTitleIcons ? 'none' : undefined} sx={subSectionTitleSx}>
            <AiOutlineAndroid fontSize={32} />
          </Box>
          <div>
            {appsGridRows.length > 0 ? (
              <>
                <LegacyDetailsList
                  hideIconsColumn
                  items={[
                    {
                      label: 'All app status',
                      value: (
                        <UpdatedAlertWithTooltip value={deviceInfo?.status.appsUpdated} statusType='apps' />
                      ),
                    },
                  ]}
                />
                <DataGrid
                  sx={{
                    marginBottom: 1,
                    maxWidth: 500,
                  }}
                  autoHeight
                  disableVirtualization
                  density='compact'
                  columns={appsGridCols}
                  rows={appsGridRows}
                />
              </>
            ) : (
              <DetailsListEmpty content='No applications found' />
            )}
          </div>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h5'>Configuration</Typography>
        </Box>
        <Box sx={subSectionSx}>
          <Box display={hideTitleIcons ? 'none' : undefined} sx={subSectionTitleSx}>
            <FaRegFileCode fontSize={32} />
          </Box>
          <LegacyDetailsList hideIconsColumn items={configItems} />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h5'>OS</Typography>
        </Box>
        <Box sx={subSectionSx}>
          <Box display={hideTitleIcons ? 'none' : undefined} sx={subSectionTitleSx}>
            <UxMason sx={{ fontSize: 32 }} />
          </Box>
          <LegacyDetailsList hideIconsColumn items={osItems} />
        </Box>
      </Box>
    </div>
  );
}

interface SoftwareInfoViewProps {
  deviceInfo?: DeviceModel;
  hideTitleIcons?: boolean;
  projectViewRoute?: Route;
}
